import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Subject, firstValueFrom } from 'rxjs';

export interface PhotoDescriptor { id: string; version: string; mainUrl: string; thumbnailUrl: string; sortOrder: number; isCover: boolean; framing?: { x: number; y: number; size: number }; }
export interface PhotoState { revision: string; canManage: boolean; photos: PhotoDescriptor[]; }
export interface PhotoCrop { x: number; y: number; size: number; }
export interface PortalPhotoSelection { assetId: string; include: boolean; revision: string; unitId?: string; replacePhotoId?: string; crop?: PhotoCrop; }
@Injectable({ providedIn: 'root' })
export class PhotosApiService {
  private readonly http = inject(HttpClient);
  readonly changed = new Subject<string>();
  private readonly images = new Map<string, Promise<string>>();
  private readonly references = new Map<string, number>();
  private readonly resolved = new Map<string, string>();
  private readonly urls = new Set<string>();
  private generation = 0;
  private base(id: string, unit: boolean) { return `${environment.apiUrl}/${unit ? 'OrganizationUnit' : 'people'}/${id}/${unit ? 'photos' : 'photo'}`; }
  state(id: string, unit = false) { return firstValueFrom(this.http.get<PhotoState>(this.base(id, unit) + (unit ? '' : '/info'))); }
  async image(path: string): Promise<string> {
    this.references.set(path, (this.references.get(path) ?? 0) + 1);
    let request = this.images.get(path);
    if (!request) {
      const generation = this.generation;
      request = firstValueFrom(this.http.get(`${environment.apiUrl}${path}`, { responseType: 'blob' })).then(blob => {
        if (generation !== this.generation) throw new Error('La sesión de imágenes cambió.');
        if (this.images.get(path) !== request) throw new Error('La imagen ya cambió.');
        if (!this.references.has(path)) throw new Error('La imagen ya no está visible.');
        const url = URL.createObjectURL(blob); this.urls.add(url); return url;
      }).catch(error => { if (this.images.get(path) === request) this.images.delete(path); throw error; });
      this.images.set(path, request);
    }
    const url = await request;
    if (this.images.get(path) !== request || !this.references.has(path)) { URL.revokeObjectURL(url); this.urls.delete(url); throw new Error('La imagen ya no está visible.'); }
    this.resolved.set(path, url);
    return url;
  }
  release(path: string) {
    const count = (this.references.get(path) ?? 0) - 1;
    if (count > 0) { this.references.set(path, count); return; }
    this.references.delete(path); this.images.delete(path);
    const url = this.resolved.get(path);
    if (url) { URL.revokeObjectURL(url); this.urls.delete(url); this.resolved.delete(path); }
  }
  clear() { ++this.generation; this.urls.forEach(url => URL.revokeObjectURL(url)); this.urls.clear(); this.images.clear(); this.references.clear(); this.resolved.clear(); }
  async upload(id: string, unit: boolean, state: PhotoState, file: File, crop: PhotoCrop, photo?: PhotoDescriptor) {
    const body = new FormData(); body.append('file', file); body.append('revision', state.revision);
    body.append('cropX', `${crop.x}`); body.append('cropY', `${crop.y}`); body.append('cropSize', `${crop.size}`);
    const url = this.base(id, unit) + (unit && photo ? `/${photo.id}` : '');
    const result = await firstValueFrom(unit && !photo ? this.http.post<PhotoState>(url, body) : this.http.put<PhotoState>(url, body));
    this.changed.next(id); return result;
  }
  async remove(id: string, unit: boolean, state: PhotoState, photo: PhotoDescriptor) {
    await firstValueFrom(this.http.delete(this.base(id, unit) + (unit ? `/${photo.id}` : ''), { headers: { 'X-Photo-Revision': state.revision } }));
    this.changed.next(id);
  }
  async gallery(id: string, state: PhotoState, coverId: string, ordered: PhotoDescriptor[]) {
    const result = await firstValueFrom(this.http.patch<PhotoState>(this.base(id, true), {
      revision: state.revision, coverId, orderedIds: ordered.map(p => p.id)
    }));
    this.changed.next(id); return result;
  }
}
